import _extends from "@babel/runtime/helpers/extends";
import styled from "@emotion/styled";
import { border, ursaColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import React, { forwardRef } from "react";
import { CollectionMultiIcon, CollectionSingleIcon, FormsIcon, PortfolioIcon, SheetIcon, WorkAppIcon, WorkspacesIcon } from "../../../generated";
import { Icon } from "../../icon/Icon";
import DashboardsIcon20 from "./DashboardsIcon20";
import DashboardsIcon26 from "./DashboardsIcon26";
import ReportsIcon20 from "./ReportsIcon20";
import ReportsIcon26 from "./ReportsIcon26";

/**
 * This component uses a discriminated union type which ensures that 40px sizes are available for all icons except the collection icons, which only offer 28 + 32 size options
 * The @variant prop determines what sizes will be available, which provides typescript linting errors when trying to use collections icons that don't offer 40pc sizes for an improved DX
 */

/**
 * A function that returns an inner icon size based on the container size
 * (container size of 28 || 32 === inner size of 20, while container size of 40 === inner size of 26)
 */
const getInnerIconSize = (sizeProp, variant) => {
  if (variant.includes("collection")) {
    return 20;
  }
  return sizeProp === 28 || sizeProp === 32 ? 20 : 26;
};

// Map each variant to the icon and bg color it should represent for easy lookup
const variantMap = {
  dashboard: {
    icon: {
      20:
      /*#__PURE__*/
      // @TODO - this icon should be migrated to the DashboardsIcon when addeed in V5
      React.createElement(Icon, {
        size: 20,
        color: color("color--background--spectrum-green--stronger", ursaColors.green600)
      }, /*#__PURE__*/React.createElement(DashboardsIcon20, null)),
      26:
      /*#__PURE__*/
      // @TODO - this icon should be migrated to the DashboardsIcon when addeed in V5
      React.createElement(Icon, {
        size: 26,
        color: color("color--background--spectrum-green--stronger", ursaColors.green600)
      }, /*#__PURE__*/React.createElement(DashboardsIcon26, null))
    },
    background: color("color--background--spectrum-green--weakest", ursaColors.green75)
  },
  "collection-multi": {
    icon: {
      20: /*#__PURE__*/React.createElement(CollectionMultiIcon, {
        size: 20,
        color: color("color--icons--neutral--stronger", ursaColors.neutral600)
      })
    },
    background: color("color--background--neutral--default", ursaColors.neutral75)
  },
  "collection-single": {
    icon: {
      20: /*#__PURE__*/React.createElement(CollectionSingleIcon, {
        size: 20,
        color: color("color--background--spectrum-yellow--strong", ursaColors.yellow500)
      })
    },
    background: color("color--background--spectrum-yellow--weakest", ursaColors.yellow100)
  },
  form: {
    icon: {
      20: /*#__PURE__*/React.createElement(FormsIcon, {
        size: 20,
        color: color("color--background--spectrum-orchid--stronger", ursaColors.orchid600)
      }),
      26: /*#__PURE__*/React.createElement(FormsIcon, {
        size: 26,
        color: color("color--background--spectrum-orchid--stronger", ursaColors.orchid600)
      })
    },
    background: color("color--background--spectrum-orchid--weakest", ursaColors.orchid50)
  },
  portfolio: {
    icon: {
      20: /*#__PURE__*/React.createElement(PortfolioIcon, {
        size: 20,
        color: color("color--background--spectrum-teal--strongest", ursaColors.teal700)
      }),
      26: /*#__PURE__*/React.createElement(PortfolioIcon, {
        size: 26,
        color: color("color--background--spectrum-teal--strongest", ursaColors.teal700)
      })
    },
    background: color("color--background--spectrum-teal--weakest", ursaColors.teal50)
  },
  report: {
    icon: {
      20:
      /*#__PURE__*/
      // @TODO - this icon should be migrated to the ReportsIcon when added in V5
      React.createElement(Icon, {
        size: 20,
        color: color("color--background--spectrum-orange--strong", ursaColors.orange500)
      }, /*#__PURE__*/React.createElement(ReportsIcon20, null)),
      26:
      /*#__PURE__*/
      // @TODO - this icon should be migrated to the ReportsIcon when added in V5
      React.createElement(Icon, {
        size: 26,
        color: color("color--background--spectrum-orange--strong", ursaColors.orange500)
      }, /*#__PURE__*/React.createElement(ReportsIcon26, null))
    },
    background: color("color--background--spectrum-orange--weakest", ursaColors.orange75)
  },
  sheet: {
    icon: {
      20: /*#__PURE__*/React.createElement(SheetIcon, {
        size: 20,
        color: color("color--background--spectrum-violet--strongest", ursaColors.violet700)
      }),
      26: /*#__PURE__*/React.createElement(SheetIcon, {
        size: 26,
        color: color("color--background--spectrum-violet--strongest", ursaColors.violet700)
      })
    },
    background: color("color--background--spectrum-violet--weakest", ursaColors.violet50)
  },
  workapp: {
    icon: {
      20: /*#__PURE__*/React.createElement(WorkAppIcon, {
        size: 20,
        color: color("color--background--spectrum-blue--strongest", ursaColors.blue700)
      }),
      26: /*#__PURE__*/React.createElement(WorkAppIcon, {
        size: 26,
        color: color("color--background--spectrum-blue--strongest", ursaColors.blue700)
      })
    },
    background: color("color--background--spectrum-blue--weakest", ursaColors.blue50)
  },
  workspace: {
    icon: {
      20: /*#__PURE__*/React.createElement(WorkspacesIcon, {
        size: 20,
        color: color("color--background--spectrum-azure--stronger", ursaColors.azure600)
      }),
      26: /*#__PURE__*/React.createElement(WorkspacesIcon, {
        size: 26,
        color: color("color--background--spectrum-azure--stronger", ursaColors.azure600)
      })
    },
    background: color("color--background--spectrum-azure--weakest", ursaColors.azure75)
  }
};
export const HeroAssetIcon = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    variant,
    size,
    ...rest
  } = _ref;
  const innerIconSize = getInnerIconSize(size, variant);
  return /*#__PURE__*/React.createElement(StyledContainer, {
    size: size,
    backgroundColor: variantMap[variant].background
  }, /*#__PURE__*/React.createElement(Icon, _extends({
    size: innerIconSize,
    ref: ref
  }, rest), variantMap[variant].icon[innerIconSize]));
});
HeroAssetIcon.displayName = "HeroAssetIcon";
const StyledContainer = styled.span(_ref2 => {
  let {
    size,
    backgroundColor
  } = _ref2;
  return `
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${size}px;
    min-width: ${size}px;
    height: ${size}px;
    min-height: ${size}px;
    background-color: ${backgroundColor};
    border-radius: ${size === 40 ? border.radius40 : border.radius30};
`;
});
//# sourceMappingURL=HeroAssetIcon.js.map